<template>
    <modal-controller-base contentClass="background-gradient-background purple-light-color">
        <template #header>
            <custom-header back-button :title="$t('titleRoute.aiCall')">
                <template #start>
                    <ion-button @click="modalController.dismiss()" class="purple-light-color">
                        <ion-icon :icon="chevronBack" />
                        {{ $t("button.back") }}
                    </ion-button>
                </template>
            </custom-header>
        </template>

        <ion-grid class="p-2" :class="{ 'center-web': !isApp() }">
            <ion-row>
                <ion-col class="center py-2">
                    <div class="picture-block">
                        <img src="assets/image/octoBot.png" class="picture octo-shadow" />
                    </div>
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="fs-smaller purple-light-color">
                    {{ $t(`alert.selectSupplier`) }}
                </ion-col>
            </ion-row>

            <ion-row :class="{ 'ion-justify-content-center': !isApp() }">
                <ion-col :class="{ 'web-input-width': !isApp() }">
                    <!-- <select-simple :model-value="supplierStore.selectedSupplierId" -->
                    <select-simple
                        :model-value="aiCallForm.supplierId" @change="changeSupplierId"
                        class="grey4-grey4-background inherit-background purple-purple-color max-width chevron-select unset-max-width"
                        :placeholder="$t('messages.setting.delivererSelect')"
                        :options="aiSupportedSuppliers"
                        valueKey="id" textKey="name"
                        :disabled="aiSupportedSuppliers.length <= 1" />
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="fs-smaller purple-light-color">
                    {{ $t(`label.aiCallbackPhone`) }}
                </ion-col>
            </ion-row>

            <ion-row :class="{ 'ion-justify-content-center': !isApp() }">
                <ion-col class="center-x" :class="{ 'web-input-width': !isApp() }">
                    <!-- <input-simple class="grey4-grey4-background inherit-background purple-purple-color unset-max-width"
                        placeholder="+49 XXX XXXXXXX" :type="INPUT_TYPE.TEL" :inputmode="INPUT_MODE.TEL"
                        v-model="aiCallForm.phone" :validate-option="{ name: 'phone', label: $t('label.phone') }" /> -->
                    <phone-dial-code-dropdown v-model:phone="aiCallForm.phone" init-coutry="de" @valid="onPhoneValid" />
                </ion-col>
            </ion-row>

            <ion-row class="mt-2">
                <ion-col class="fs-smaller purple-light-color">
                    {{ $t(`label.aiCallLanguage`) }}
                </ion-col>
            </ion-row>

            <ion-row>
                <ion-col class="center-x">
                    <language-dropdown :language="aiCallForm.language" :changeFunction="changeCallLanguage" class="center-x" style="width: 100%" />
                </ion-col>
            </ion-row>

            <ion-row class="mt-4">
                <ion-col class="center-x">
                    <button-fix :disabled="!meta.valid"
                        class="coral-light-background inherit-background light-coral-color inherit-color"
                        @click="aiCallRequest">
                        {{ $t('button.requestAiCall') }}
                    </button-fix>
                </ion-col>
            </ion-row>


        </ion-grid>
    </modal-controller-base>
</template>

<script setup lang="ts">
import { IonCol, IonGrid, IonRow, modalController, IonButton, IonIcon } from "@ionic/vue";
import { computed, onMounted, reactive, ref } from "vue";
import { useForm } from "vee-validate";

import InputSimple from "@/components/input/InputSimple.vue";
import SelectSimple from "@/components/selects/SelectSimple.vue";
import ButtonFix from "@/components/button/ButtonFix.vue";

import ModalControllerBase from "@/components/modal/modalControllerModals/ModalControllerBase.vue";
import CustomHeader from "@/components/basePageElements/CustomHeader.vue";
import LanguageDropdown from "@/components/selects/LanguageDropdown.vue";

import PhoneDialCodeDropdown from "@/components/selects/PhoneDialCodeDropdown.vue";

import { useLocationStore } from "@/stores/locations";
import { useUserStore } from "@/stores/user";
import { useSupplierStore } from "@/stores/suppliers";

import { interactionFeedbackToast } from "@/functions/baseToast";
import { isApp } from "@/functions/enviroment";

import { APP_LANGUAGES, INPUT_MODE, INPUT_TYPE } from "@/types/enum/ionicEnum";
import { chevronBack } from "ionicons/icons";
import { requestRaw } from "@/api-connectors/basicRequests";

const supplierStore = useSupplierStore()

const locationStore = useLocationStore()
const userStore = useUserStore()

const phoneIsValid = ref(false);

const { meta, validateField } = useForm({
    validationSchema: {
        locationId: 'required',
        supplierId: 'required',
        language: 'required',
        phone: (value: string) => {
            if (!value || value === '') return false
            if (!phoneIsValid.value) return false
            return true // valid
        }
    }
})

function onPhoneValid(valid: boolean) {
    phoneIsValid.value = valid
    validateField('phone')
}

interface AiCall {
    locationId: string,
    supplierId: string,
    language: APP_LANGUAGES,
    phone: string,
}

const aiCallForm = reactive<AiCall>({
    locationId: '',
    supplierId: '',
    language: APP_LANGUAGES['DE'],
    phone: '',
})

const aiSupportedSuppliers = computed(() => supplierStore.suppliers.data.filter((supplier) => supplier.hasCallBot))

const changeSupplierId = (supplierId: unknown) => {
    if (typeof supplierId === "string") {
        Object.assign(aiCallForm, { ...aiCallForm, supplierId })
    }
}

const changeCallLanguage = (language: string) => {
    Object.assign(aiCallForm, { ...aiCallForm, language })
}

const setup = (): void => {
    const oneSupplierId = aiSupportedSuppliers.value.length === 1 ? aiSupportedSuppliers.value[0].id : '';

    if (locationStore.activeLocation?.id) Object.assign(aiCallForm, {
        locationId: locationStore.activeLocation.id,
        supplierId: oneSupplierId || '',
        language: userStore.sessionInfo?.callBot?.language || localStorage.getItem('localeLanguage') || 'DE',
        phone: userStore.sessionInfo.callBot?.phone || userStore.sessionInfo.phone || locationStore.activeLocation.phone
    })
}

const aiCallRequest = async (): Promise<void> => {
    const { locationId, supplierId, language, phone } = aiCallForm
    const form = {
        language: language,
        phone: phone.replace(/[^\d+]/g, ''), /* normalize */
    }

    const response = await requestRaw({
        method: 'POST',
        route: `locations/${locationId}/suppliers/${supplierId}/callMe`,
        body: form
    })

    userStore.getMe() // update saved data

    await interactionFeedbackToast(Boolean(response), {
        type: 'aiCall',
        id: 'ai-call',
    }, { cssClass: 'toast-offset-50' }, true)

    setTimeout(() => modalController.dismiss(), 2000)
}

onMounted(() => setup())
</script>

<style scoped>
.picture-block {
    width: 150px;
    height: 150px;
}

.picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    border: 2px solid var(--ion-color-light);
}

@media (min-width: 500px) and (min-height: 500px) {
    .unset-max-width {
		max-width: unset;
	}
}

@media (orientation: landscape) and (min-height: 500px) {
    .unset-max-width {
		max-width: unset;
	}
}
</style>
